import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  FieldGrid,
  FormMethods,
  useFormDialog,
} from '@if/mui'
import { Stack } from '@mui/material'
import { EOperationStatus } from '@src/types/constants'
import {
  CalendarSimulationAction,
  ECalendarSimulationAction,
  SignedCalendarSimulation,
} from '@src/types/loan/loan-simulation'
import { CircleCheck } from 'lucide-react'
import { JSX, useEffect, useMemo } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCalendarPreviewConfirm } from '../LoanCalendarActionPreview/LoanCalendarActionPreview-confirm'
import {
  useLoanCalendarActionPreview,
  useSettledValue,
} from '../LoanCalendarActionPreview/LoanCalendarActionPreview-recompute'
import LoanCalendarActionPreviewBody from '../LoanCalendarActionPreview/LoanCalendarActionPreviewBody'
import {
  ChangePaymentStatusSchemaType,
  getStatusThatNeedsTransactionDate,
  useChangePaymentStatusSchema,
} from './LoanCalendarPaymentStatus-schema'
import { useAvailablePaymentStatuses } from './LoanCalendarPaymentStatus-selectors'
import NsfStatusSelect from './NsfStatusSelect'

interface LoanCalendarPaymentStatusBodyProps {
  form: FormMethods<ChangePaymentStatusSchemaType>
  initialSignedSimulation: SignedCalendarSimulation
  paymentIds: Set<string>
  statusOptions: JSX.Element[]
  disabled: boolean
}

const LoanCalendarPaymentStatusBody = ({
  form,
  initialSignedSimulation,
  paymentIds,
  statusOptions,
  disabled,
}: LoanCalendarPaymentStatusBodyProps) => {
  const { t } = useTranslation()
  const { control, setValue } = form
  const originalSimulation = initialSignedSimulation.simulation

  const newStatus = useWatch({ control, name: 'newStatus' })
  const transactionDate = useWatch({ control, name: 'transactionDate' })
  const nsfFee = useWatch({ control, name: 'nsfFee' })
  const nsfStatus = useWatch({ control, name: 'nsfStatus' })
  const previewedSimulation = useWatch({ control, name: 'previewedSimulation' })
  const { isValid } = useFormState({ control })

  const isFailure = newStatus === EOperationStatus.Failed
  const needsTransactionDate = getStatusThatNeedsTransactionDate().has(newStatus)

  // The NSF fields only exist for a failure, so a status that is not one drops what the agent
  // had already typed for them rather than carrying it into an action that has no room for it.
  useEffect(() => {
    if (isFailure) return
    setValue('nsfFee', null, { shouldValidate: true })
    setValue('nsfStatus', null, { shouldValidate: true })
  }, [isFailure, setValue])

  useEffect(() => {
    if (!needsTransactionDate) setValue('transactionDate', null, { shouldValidate: true })
  }, [needsTransactionDate, setValue])

  // The selection is made of simulated ids; the action names the operations behind them.
  const paymentOperationIds = useMemo(
    () =>
      originalSimulation.operationInformations
        .filter((oi) => paymentIds.has(oi.simulatedOperationId))
        .map((oi) => oi.operation.id),
    [originalSimulation, paymentIds],
  )

  // The recompute follows the fields once they settle, and settling is what opens Confirm —
  // so what the agent is about to apply is always the calendar they are looking at.
  const settledStatus = useSettledValue(newStatus)
  const settledTransactionDate = useSettledValue(transactionDate)
  const settledNsfFee = useSettledValue(nsfFee)
  const settledNsfStatus = useSettledValue(nsfStatus)

  const action = useMemo<CalendarSimulationAction | null>(
    () =>
      isValid &&
      settledStatus !== undefined &&
      settledTransactionDate !== undefined &&
      settledNsfFee !== undefined &&
      settledNsfStatus !== undefined
        ? {
            actionType: ECalendarSimulationAction.ChangePaymentStatus,
            newStatus: settledStatus,
            nsfFee: settledNsfFee ?? 0,
            transactionDate: settledTransactionDate ?? undefined,
            nsfStatus: settledNsfStatus ?? undefined,
            paymentIds: paymentOperationIds,
          }
        : null,
    [isValid, paymentOperationIds, settledNsfFee, settledNsfStatus, settledStatus, settledTransactionDate],
  )

  const error = useLoanCalendarActionPreview({ form, initialSignedSimulation, action })

  return (
    <Stack spacing={2}>
      <LoanCalendarActionPreviewBody
        simulationBefore={originalSimulation}
        preview={previewedSimulation}
        recomputing={isValid && previewedSimulation === null}
        actionDate={transactionDate}
        error={error}
      />

      <FieldGrid columns={{ xs: 1, sm: 2 }}>
        <ControlledSelect
          name="newStatus"
          control={control}
          options={statusOptions}
          label={t('loan.payment.changeStatusPayment.newStatus')}
          disableEmptyOption
          disabled={disabled}
        />
        <ControlledDateOnlyPicker
          name="transactionDate"
          control={control}
          label={t('loan.payment.changeStatusPayment.transactionDate')}
          disabled={disabled || !needsTransactionDate}
        />
        {isFailure && (
          <ControlledNumberField
            control={control}
            name="nsfFee"
            isMoney
            disableDecimals
            label={t('loan.payment.changeStatusPayment.nsfFee')}
            disabled={disabled}
          />
        )}
        {isFailure && <NsfStatusSelect control={control} name="nsfStatus" disabled={disabled} />}
      </FieldGrid>
    </Stack>
  )
}

export const useLoanCalendarPaymentStatusDialog = (
  initialSignedSimulation: SignedCalendarSimulation,
  paymentIds: Set<string>,
  isLockedByOther: boolean,
) => {
  const { t } = useTranslation()

  // Every selected payment is on the same status — that is what the opener gates on — so the
  // first one names the transition the dialog offers.
  const currentStatus = useMemo(
    () =>
      initialSignedSimulation.simulation.operationInformations.find((oi) => paymentIds.has(oi.simulatedOperationId))
        ?.operation.operationStatusId ?? EOperationStatus.Planned,
    [initialSignedSimulation, paymentIds],
  )

  const schema = useChangePaymentStatusSchema(currentStatus)
  const statusOptions = useAvailablePaymentStatuses(currentStatus)
  const confirm = useCalendarPreviewConfirm(isLockedByOther)

  return useFormDialog<ChangePaymentStatusSchemaType, void, SignedCalendarSimulation>({
    icon: CircleCheck,
    title: t('loan.payment.changeStatusPayment.title'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    // Confirming stages a pending change on the page, it saves nothing — the page says so.
    successMessage: false,
    ...confirm,
    content: ({ form }) => (
      <LoanCalendarPaymentStatusBody
        form={form}
        initialSignedSimulation={initialSignedSimulation}
        paymentIds={paymentIds}
        statusOptions={statusOptions}
        disabled={isLockedByOther}
      />
    ),
    // The preview is the submit: Confirm applies the recompute the agent watched settle
    // rather than firing a fresh one. The gate above never lets a null through.
    onSubmit: (values) => values.previewedSimulation ?? false,
  })
}
